import React from 'react';

const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    itemsPerPage,
    totalItems,
    onPageSizeChange,
    metadata // Support for backend API format
}) => {
    // Support both direct props and metadata object from backend API
    const page = metadata?.page || currentPage || 1;
    const pageSize = metadata?.pageSize || itemsPerPage || 50;
    const pages_total = metadata?.totalPages || totalPages || 1;

    const pages = [];
    const maxVisiblePages = 5;

    // Calculate page range to show
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pages_total, startPage + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    if (pages_total <= 1 && !onPageSizeChange) return null;

    const pageSizeOptions = [10, 25, 50, 100];

    return (
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-4">
                {/* Page size selector */}
                {onPageSizeChange && (
                    <div className="flex items-center gap-2">
                        <label htmlFor="pageSize" className="text-sm text-gray-700">
                            Số lượng:
                        </label>
                        <select
                            id="pageSize"
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                            className="px-3 py-1 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Page navigation */}
                <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <button
                        onClick={() => onPageChange(page - 1)}
                        disabled={page === 1}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <i className="fas fa-chevron-left text-xs" aria-hidden></i>
                        <span className="hidden sm:inline">Trước</span>
                    </button>

                    {/* First Page */}
                    {startPage > 1 && (
                        <>
                            <button
                                onClick={() => onPageChange(1)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                            >
                                1
                            </button>
                            {startPage > 2 && (
                                <span className="px-2 text-slate-400">...</span>
                            )}
                        </>
                    )}

                    {/* Page Numbers */}
                    {pages.map((pageNum) => (
                        <button
                            key={pageNum}
                            onClick={() => onPageChange(pageNum)}
                            className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border text-sm font-medium transition ${
                                page === pageNum
                                    ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm'
                                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                            }`}
                        >
                            {pageNum}
                        </button>
                    ))}

                    {/* Last Page */}
                    {endPage < pages_total && (
                        <>
                            {endPage < pages_total - 1 && (
                                <span className="px-2 text-slate-400">...</span>
                            )}
                            <button
                                onClick={() => onPageChange(pages_total)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                            >
                                {pages_total}
                            </button>
                        </>
                    )}

                    {/* Next Button */}
                    <button
                        onClick={() => onPageChange(page + 1)}
                        disabled={page === pages_total}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <span className="hidden sm:inline">Sau</span>
                        <i className="fas fa-chevron-right text-xs" aria-hidden></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Pagination;
